import { Navigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

/**
 * Public landing page (no login required to view). A full-screen video hero
 * introduces the app, followed by a public "what it does" section and footer
 * links to the privacy policy and terms. Sign-in is offered as an action but
 * is not a gate — unauthenticated visitors (and OAuth reviewers) can read
 * everything about the app without authenticating.
 */
const FEATURES: Array<{ icon: string; title: string; body: string }> = [
  {
    icon: "📍",
    title: "Discover meets near you",
    body: "Browse upcoming wrestling meets, sort by distance from home, and filter by radius so you only see the events worth the drive.",
  },
  {
    icon: "✅",
    title: "RSVP & see who's going",
    body: "Mark whether you're attending and view the attendee list within your team so families can plan together.",
  },
  {
    icon: "🚗",
    title: "Coordinate carpools",
    body: "Offer or request rides for a meet and sort out transportation with other parents on your team.",
  },
  {
    icon: "✉️",
    title: "Invite other parents",
    body: "Share a meet with other families by email or text and let them RSVP from a simple invite link.",
  },
  {
    icon: "📅",
    title: "Add meets to your calendar",
    body: "Link Google Calendar to add meets you're attending in one tap, or download an .ics file — your choice, fully optional.",
  },
  {
    icon: "🏅",
    title: "Track match history",
    body: "Keep a private record of your wrestler's matches and results, visible only to your household and team.",
  },
];

export function Home() {
  const { isAuthenticated, login } = useAuth();
  if (isAuthenticated) return <Navigate to="/meets" replace />;

  return (
    <div className="landing">
      {/* ---------- Hero ---------- */}
      <section className="landing-hero">
        <video
          className="signin-video"
          src="/media/bg-landscape.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        <div className="signin-overlay" aria-hidden="true" />
        <div className="signin-panel">
          <h1 style={{ fontSize: "2.6rem", marginBottom: "0.5rem" }}>
            🤼 Wrestling Meet Tracker
          </h1>
          <p
            className="muted"
            style={{ fontSize: "1.15rem", marginBottom: "1.5rem", maxWidth: 560 }}
          >
            The all-in-one app for wrestling parents and coaches: discover
            meets, see who's going, RSVP, coordinate carpools, invite other
            parents, add meets to your calendar, and track match history.
          </p>
          <div className="row" style={{ justifyContent: "center" }}>
            <button className="btn-lift" onClick={() => login()}>
              Sign in
            </button>
            <button
              className="secondary btn-lift"
              onClick={() => login("Google")}
            >
              Sign in with Google
            </button>
          </div>
          <p className="muted" style={{ marginTop: "0.75rem", fontSize: "0.9rem" }}>
            New here? Scroll down to learn what the app does.
          </p>
        </div>
      </section>

      {/* ---------- What it does (public) ---------- */}
      <section className="landing-section">
        <div className="container">
          <h2 style={{ textAlign: "center", fontSize: "1.8rem" }}>
            Everything a wrestling family needs in one place
          </h2>
          <p
            className="muted"
            style={{ textAlign: "center", maxWidth: 640, margin: "0.5rem auto 2rem" }}
          >
            Wrestling Meet Tracker helps parents and coaches stay organized
            across a busy season — from finding meets to getting everyone there.
          </p>
          <div className="landing-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="card landing-feature">
                <div className="landing-feature-icon" aria-hidden="true">
                  {f.icon}
                </div>
                <h3 style={{ margin: "0.25rem 0 0.4rem" }}>{f.title}</h3>
                <p className="muted" style={{ margin: 0 }}>
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Google Calendar note (public, matches OAuth scope) ---------- */}
      <section className="landing-section landing-section-alt">
        <div className="container" style={{ maxWidth: 720, textAlign: "center" }}>
          <h2 style={{ fontSize: "1.5rem" }}>Optional Google Calendar sync</h2>
          <p className="muted">
            If you choose to connect Google Calendar, the app adds the meets you
            opt to save as calendar events and keeps them up to date. We only
            create and manage the events you add — we never read your other
            calendar events. You can disconnect Google at any time from your
            profile.
          </p>
        </div>
      </section>

      {/* ---------- Footer (public links) ---------- */}
      <footer className="landing-footer">
        <div className="container">
          <span className="muted">
            © {new Date().getFullYear()} Wrestling Meet Tracker
          </span>
          <nav className="landing-footer-links">
            <a href="/privacy.html">Privacy Policy</a>
            <a href="/terms.html">Terms of Service</a>
            <a href="mailto:seamus.owens@gmail.com">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
